import React, { useState, useEffect } from 'react';
import { coursesAPI, Course } from '../../api/api';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import './AdminPanel.css';

export const CoursesAdmin: React.FC = () => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    // Обработчик клика на кнопку удаления
    const handleDeleteClick = (id: number) => {
        setItemToDelete(id);
        setShowConfirm(true);
    };

    // Обработчик подтверждения удаления
    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            try {
                await coursesAPI.delete(itemToDelete);
                loadCourses();
            } catch (err) {
                alert('Ошибка при удалении');
                console.error(err);
            } finally {
                setShowConfirm(false);
                setItemToDelete(null);
            }
        }
    };

    // Обработчик отмены удаления
    const handleCancelDelete = () => {
        setShowConfirm(false);
        setItemToDelete(null);
    };

    // Форма
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        image: null as File | null,
        duration: '',
        count: '',
        timeLesson: ''
    });

    // Загрузка курсов
    useEffect(() => {
        loadCourses();
    }, []);

    const loadCourses = async () => {
        try {
            setLoading(true);
            const data = await coursesAPI.getAll();
            setCourses(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить курсы');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Обработка ввода
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, image: e.target.files![0] }));
        }
    };

    // Добавление/редактирование
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.description || !formData.price) {
            alert('Заполните все поля!');
            return;
        }

        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('description', formData.description);
        submitData.append('price', formData.price);
        if (formData.image) {
            submitData.append('image', formData.image);
        }
        submitData.append('duration', formData.duration);
        submitData.append('count', formData.count);
        submitData.append('timeLesson', formData.timeLesson);

        try {
            if (editingId) {
                submitData.append('id', editingId.toString());
                await coursesAPI.update(editingId, submitData);
            } else {
                await coursesAPI.add(submitData);
            }

            // Очистка формы и перезагрузка
            setFormData({ name: '', description: '', price: '', image: null, duration: '', count: '', timeLesson: '' });
            setShowForm(false);
            setEditingId(null);
            loadCourses();
        } catch (err) {
            alert('Ошибка при сохранении');
            console.error(err);
        }
    };

    // Редактирование
    const handleEdit = (course: Course) => {
        setFormData({
            name: course.name,
            description: course.description,
            price: course.price.toString(),
            image: null,
            duration: course.duration?.toString() || '',
            count: course.count?.toString() || '',
            timeLesson: course.timeLesson?.toString() || ''
        });
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
        setEditingId(course.id);
        setShowForm(true);
    };

    // Отмена редактирования
    const cancelEdit = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({ name: '', description: '', price: '', image: null, duration: '', count: '', timeLesson: ''  });
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="admin-panel">
            <div className="admin-panel-header">
                <h2>Управление курсами</h2>
                <button
                    className="btn-add"
                    onClick={() => {
                        setShowForm(!showForm);
                        setEditingId(null);
                        setFormData({ name: '', description: '', price: '', image: null, duration: '', count: '', timeLesson: '' });
                    }}
                >
                    {showForm ? 'Отмена' : '+ Добавить курс'}
                </button>
            </div>

            {/* Форма добавления/редактирования */}
            {showForm && (
                <form className="review-form" onSubmit={handleSubmit}>
                    <h3>{editingId ? 'Редактировать курс' : 'Новый курс'}</h3>

                    <div className="form-group">
                        <label>Название курса:</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Описание:</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={4}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Цена (₽):</label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            min="0"
                            required
                        />
                    </div>
                    <div className="form-groupFlex">
                        <div className="">
                            <label>Длительность(в неделях):</label>
                            <input
                                type="number"
                                name="duration"
                                value={formData.duration}
                                onChange={handleInputChange}
                                min="0"
                                required
                            />
                        </div>
                        <div className="">
                            <label>Количество уроков:</label>
                            <input
                                type="number"
                                name="count"
                                value={formData.count}
                                onChange={handleInputChange}
                                min="0"
                                required
                            />
                        </div>
                        <div className="">
                            <label>Продолжительность урока(в мин.):</label>
                            <input
                                type="number"
                                name="timeLesson"
                                value={formData.timeLesson}
                                onChange={handleInputChange}
                                min="0"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Изображение:</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                        {formData.image && (
                            <span className="file-info">Выбран файл: {formData.image.name}</span>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-save">
                            {editingId ? 'Сохранить' : 'Добавить'}
                        </button>
                        <button type="button" className="btn-cancel" onClick={cancelEdit}>
                            Отмена
                        </button>
                    </div>
                </form>
            )}

            {/* Список курсов */}
            <div className="courses-list">
                {courses.length === 0 ? (
                    <p>Нет курсов</p>
                ) : (
                    courses.map(course => (
                        <div key={course.id} className="course-item-admin">
                            {course.image && (
                                <img
                                    src={course.image.startsWith('data:') ? course.image : `data:image/jpeg;base64,${course.image}`}
                                    alt={course.name}
                                    className="course-image"
                                />
                            )}
                            <div className="course-content">
                                <h4>{course.name}</h4>
                                <p>{course.description}</p>
                                <div className="course-price">{course.price} ₽</div>
                            </div>
                            <div className="course-actions">
                                <button
                                    className="btn-edit"
                                    onClick={() => handleEdit(course)}
                                >
                                    ✏️
                                </button>
                                <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteClick(course.id)}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Диалог подтверждения удаления */}
            {showConfirm && itemToDelete && (
                <ConfirmDialog
                    message="Удалить этот курс?"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};